/**
 * As consultas do console. Todas passam pela RLS.
 *
 * Fonte normativa: SCRIPT G7, §12 (dashboard), §13 (documentos), §14 (recepção
 * e eventos), §15 (certificados) e §104 (não relaxar RLS para a UI funcionar).
 *
 * O FILTRO POR IDENTIDADE É REDUNDANTE, E FICA
 *
 *   Cada consulta filtra por `fiscal_identity_id`, e a RLS já filtraria. A
 *   redundância é deliberada e tem dois motivos.
 *
 *   O primeiro é que a política responde "quais identidades você pode ver" e a
 *   tela pergunta "o que há NESTA identidade" — um usuário com duas empresas vê
 *   as duas pela RLS e escolhe uma na interface. Sem o filtro, o console
 *   misturaria dados de duas empresas na mesma tela.
 *
 *   O segundo é que o filtro não é a proteção. Se alguém trocar o
 *   `fiscal_identity_id` no devtools, a RLS recusa. É por isso que ele pode ser
 *   redundante: a segurança está no banco, e aqui é escopo de tela.
 *
 * O QUE NÃO EXISTE AQUI
 *
 *   Nenhuma escrita. Nenhuma chamada que emita, cancele, substitua ou registre
 *   evento. O §127 é explícito: nenhuma emissão fiscal é necessária ou permitida
 *   para validar o G7, e o guard `frontend-sem-ato-fiscal` recusa o contrário.
 */

import { getSupabase } from './supabase.js';

export interface NfseDocumentRow {
  readonly id: string;
  readonly environment: string;
  readonly provider_id: string;
  readonly serie: string | null;
  readonly numero: string | null;
  readonly nfse_number: string | null;
  readonly provider_rps_number: string | null;
  readonly provider_document_ref: string | null;
  readonly provider_protocol: string | null;
  readonly dps_id: string | null;
  readonly access_key: string | null;
  readonly status: string;
  readonly rejection_code: string | null;
  readonly rejection_message: string | null;
  readonly issuer_municipality_code: string;
  readonly authorized_at: string | null;
  readonly cancelled_at: string | null;
  readonly retro_custody: boolean | null;
  readonly created_at: string;
}

export interface NfeDocumentRow {
  readonly id: string;
  readonly environment: string;
  readonly document_type: string;
  readonly model: string;
  readonly serie: string | null;
  readonly number: string | null;
  readonly access_key: string | null;
  readonly status: string;
  readonly cstat: string | null;
  readonly xmotivo: string | null;
  readonly protocol_number: string | null;
  readonly issue_datetime: string | null;
  readonly authorized_at: string | null;
  readonly created_at: string;
}

export interface CertificateRow {
  readonly id: string;
  readonly certificate_type: string;
  readonly serial_number: string;
  readonly subject_dn: string;
  readonly issuer_dn: string;
  readonly subject_tax_id: string | null;
  readonly not_before: string;
  readonly not_after: string;
  readonly status: string;
  readonly fingerprint_sha256: string;
}

export interface ArtifactRow {
  readonly id: string;
  readonly kind: string;
  readonly source: string;
  readonly ingestion_origin: string | null;
  readonly media_type: string | null;
  readonly byte_size: number | null;
  readonly sha256: string;
  readonly created_at: string;
  readonly nfse_document_id: string | null;
  readonly document_id: string | null;
}

export interface EventRow {
  readonly id: string;
  readonly document_id: string | null;
  readonly event_type: string;
  readonly event_code: string | null;
  readonly status: string;
  readonly sequence_number: number | null;
  readonly cstat: string | null;
  readonly xmotivo: string | null;
  readonly registered_at: string | null;
  readonly created_at: string;
}

export interface ProviderInstanceRow {
  readonly id: string;
  readonly provider_id: string;
  readonly environment: string;
  readonly municipality_code: string | null;
  readonly credentialing: string | null;
  readonly endpoint_url: string | null;
  readonly status: string | null;
  readonly notes: string | null;
}

/**
 * O erro de consulta vira exceção, sempre.
 *
 * `@supabase/supabase-js` devolve `{ data, error }` e não lança. Quem esquecer
 * de olhar `error` recebe `data === null` e, num `?? []`, uma lista vazia — que
 * é exatamente a confusão que o §110 proíbe.
 *
 * Transformar em exceção faz o TanStack Query classificar como `isError`, e a
 * tela mostra falha em vez de "nada encontrado".
 */
function ouFalhe<T>(resultado: { data: unknown; error: { message: string } | null }): T {
  if (resultado.error !== null) {
    throw new Error(resultado.error.message);
  }
  if (resultado.data === null || resultado.data === undefined) {
    throw new Error('A consulta não devolveu dados nem erro. Estado indeterminado.');
  }
  /*
   * A conversão é a fronteira entre o que o PostgREST devolve e o que este
   * módulo promete, e ela é declarada aqui, uma vez.
   *
   * O SDK tipa o retorno de um `select` por string como uma união que inclui
   * erro; sem esquema gerado ele não tem como saber a forma das linhas.
   * Espalhar a conversão por seis funções esconderia seis conversões — aqui ela
   * fica num lugar só, com o nome do risco escrito em cima.
   *
   * O que garante a forma não é o tipo: é a lista explícita de colunas em cada
   * consulta, mais o grant da 0037, que faz o banco recusar coluna fora dela.
   */
  return resultado.data as T;
}

export async function fetchNfseDocuments(identityId: string): Promise<NfseDocumentRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('nfse_documents')
      .select(
        'id, environment, provider_id, serie, numero, nfse_number, provider_rps_number, ' +
          'provider_document_ref, provider_protocol, dps_id, access_key, status, ' +
          'rejection_code, rejection_message, issuer_municipality_code, ' +
          'authorized_at, cancelled_at, retro_custody, created_at',
      )
      .eq('fiscal_identity_id', identityId)
      .order('created_at', { ascending: false })
      .limit(500),
  );
}

export async function fetchNfeDocuments(identityId: string): Promise<NfeDocumentRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('fiscal_documents')
      .select(
        'id, environment, document_type, model, serie, number, access_key, status, ' +
          'cstat, xmotivo, protocol_number, issue_datetime, authorized_at, created_at',
      )
      .eq('fiscal_identity_id', identityId)
      .order('created_at', { ascending: false })
      .limit(500),
  );
}

export async function fetchCertificates(identityId: string): Promise<CertificateRow[]> {
  /*
   * A lista de colunas é a lista do §15, e nada além.
   *
   * `storage_bucket` e `storage_path` não estão aqui, e não estariam mesmo que
   * alguém os pedisse: o grant da 0037 não os concede, e o PostgREST recusaria a
   * consulta inteira. A tela e o banco concordam sobre o mesmo limite.
   */
  return ouFalhe(
    await getSupabase()
      .from('fiscal_certificates')
      .select(
        'id, certificate_type, serial_number, subject_dn, issuer_dn, subject_tax_id, ' +
          'not_before, not_after, status, fingerprint_sha256',
      )
      .eq('fiscal_identity_id', identityId)
      .order('not_after', { ascending: true })
      .limit(200),
  );
}

export async function fetchArtifacts(identityId: string): Promise<ArtifactRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('fiscal_artifacts')
      .select(
        'id, kind, source, ingestion_origin, media_type, byte_size, sha256, created_at, ' +
          'nfse_document_id, document_id',
      )
      .eq('fiscal_identity_id', identityId)
      .order('created_at', { ascending: false })
      .limit(500),
  );
}

export async function fetchEvents(identityId: string): Promise<EventRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('fiscal_document_events')
      .select(
        'id, document_id, event_type, event_code, status, sequence_number, cstat, xmotivo, ' +
          'registered_at, created_at',
      )
      .eq('fiscal_identity_id', identityId)
      .order('created_at', { ascending: false })
      .limit(200),
  );
}

export async function fetchProviderInstances(identityId: string): Promise<ProviderInstanceRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('nfse_provider_instances')
      .select(
        'id, provider_id, environment, municipality_code, credentialing, endpoint_url, status, notes',
      )
      .eq('fiscal_identity_id', identityId),
  );
}

/* ---------------------------------------------------------------------------
   Recepção de DF-e — G8.2-B, §22
   --------------------------------------------------------------------------- */

/**
 * O estado da captura, e não só os documentos.
 *
 * A TELA PRECISA DISTINGUIR DUAS COISAS QUE SE PARECEM
 *
 *   "Nenhum documento novo na última sincronização"  — a captura rodou e o
 *   Ambiente Nacional respondeu que não há nada. É informação sobre a empresa.
 *
 *   "Captura ainda não executada"                     — ninguém perguntou nada
 *   a lugar nenhum. Não é informação sobre a empresa: é ausência de informação.
 *
 *   Mostrar as duas como uma lista vazia faria a segunda parecer a primeira — e
 *   o operador concluiria que não chegou nota quando, na verdade, ninguém foi
 *   olhar. É o mesmo desenho de `AsyncPanel`, onde perguntar "está vazio?"
 *   antes de "falhou?" transforma erro em ausência.
 */
export interface DfeCursorRow {
  readonly id: string;
  readonly environment: string;
  readonly service: string;
  readonly last_nsu: string;
  readonly max_nsu: string | null;
  readonly last_success_at: string | null;
  readonly last_cstat: string | null;
  readonly last_xmotivo: string | null;
  readonly next_allowed_at: string | null;
}

export interface DfeSyncRunRow {
  readonly id: string;
  readonly environment: string;
  readonly status: string;
  readonly started_at: string;
  readonly finished_at: string | null;
  readonly cstat: string | null;
  readonly xmotivo: string | null;
  readonly starting_nsu: string;
  readonly ending_nsu: string | null;
  readonly documents_seen: number;
  readonly documents_inserted: number;
}

export interface DfeReceivedDocumentRow {
  readonly id: string;
  readonly environment: string;
  readonly access_key: string;
  readonly issuer_tax_id: string;
  readonly issuer_name: string;
  readonly issue_datetime: string;
  readonly total_amount: string;
  readonly situation_code: string | null;
  readonly completeness: string;
  readonly manifestation_status: string;
  readonly first_nsu: string | null;
}

export async function fetchDfeCursors(identityId: string): Promise<DfeCursorRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('dfe_distribution_states')
      .select(
        'id, environment, service, last_nsu, max_nsu, last_success_at, ' +
          'last_cstat, last_xmotivo, next_allowed_at',
      )
      .eq('fiscal_identity_id', identityId),
  );
}

export async function fetchDfeSyncRuns(identityId: string): Promise<DfeSyncRunRow[]> {
  /*
   * `lease_owner` e `failure_reason` não estão aqui — e não estariam mesmo que
   * alguém os pedisse: o grant por coluna da 0038 os deixou de fora, e o banco
   * recusaria. A tela mostra QUE um item falhou e com que status; o texto do
   * erro é diagnóstico de servidor.
   */
  return ouFalhe(
    await getSupabase()
      .from('dfe_sync_runs')
      .select(
        'id, environment, status, started_at, finished_at, cstat, xmotivo, ' +
          'starting_nsu, ending_nsu, documents_seen, documents_inserted',
      )
      .eq('fiscal_identity_id', identityId)
      .order('started_at', { ascending: false })
      .limit(50),
  );
}

export async function fetchDfeReceivedDocuments(
  identityId: string,
): Promise<DfeReceivedDocumentRow[]> {
  return ouFalhe(
    await getSupabase()
      .from('dfe_received_documents')
      .select(
        'id, environment, access_key, issuer_tax_id, issuer_name, issue_datetime, ' +
          'total_amount, situation_code, completeness, manifestation_status, first_nsu',
      )
      .eq('fiscal_identity_id', identityId)
      .order('issue_datetime', { ascending: false })
      .limit(500),
  );
}
